import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { notificationsAPI } from "../services/api";
import { Notification, NotificationsResponse } from "../types";
import { useApp } from "./AppContext";
import { queryClient, queryKeys } from "../lib/queryClient";

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  refreshNotifications: () => Promise<void>;
  clearNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const { updateNotifications } = useApp();

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      // Check if user is authenticated
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("No authentication token, skipping notifications fetch");
        setNotifications([]);
        setUnreadCount(0);
        return;
      }

      setIsLoading(true);
      const response: NotificationsResponse =
        await notificationsAPI.getNotifications({
          page: 1,
          limit: 50,
        });

      // Safe destructuring with default values
      const {
        notifications: notificationsList,
        unread_count: unreadCountValue,
      } = response || { notifications: [], unread_count: 0 };

      setNotifications(notificationsList || []);
      setUnreadCount(unreadCountValue || 0);

      // Update app context
      updateNotifications({
        unreadCount: unreadCountValue || 0,
        lastChecked: new Date(),
      });
    } catch (_error) {
      console.error("Failed to fetch notifications:", _error);
      // Set default values on error
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [updateNotifications]);

  // Otomatik güncelleme için useEffect
  useEffect(() => {
    // İlk yükleme
    fetchNotifications();

    // Her 15 saniyede bir güncelleme
    const interval = setInterval(() => {
      fetchNotifications();
    }, 15000);

    // Sayfa görünür olduğunda güncelleme
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchNotifications();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchNotifications]);

  // Mark notification as read
  const markAsRead = useCallback(
    async (id: string) => {
      try {
        // Check if user is authenticated
        const token = localStorage.getItem("token");
        if (!token) {
          console.log("No authentication token, skipping mark as read");
          return;
        }

        await notificationsAPI.markAsRead(id);

        // Optimistically update local state
        setNotifications((prev) =>
          prev.map((notification) =>
            notification._id === id
              ? { ...notification, is_read: true }
              : notification
          )
        );

        setUnreadCount((prev) => Math.max(0, prev - 1));

        // Update app context
        updateNotifications({
          unreadCount: Math.max(0, unreadCount - 1),
          lastChecked: new Date(),
        });

        // Invalidate notifications query
        queryClient.invalidateQueries({
          queryKey: queryKeys.notifications.lists(),
        });
      } catch (_error) {
        console.error("Failed to mark notification as read:", _error);
      }
    },
    [unreadCount, updateNotifications]
  );

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      // Check if user is authenticated
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("No authentication token, skipping mark all as read");
        return;
      }

      await notificationsAPI.markAllAsRead();

      // Optimistically update local state
      setNotifications((prev) =>
        prev.map((notification) => ({ ...notification, is_read: true }))
      );

      setUnreadCount(0);

      // Update app context
      updateNotifications({
        unreadCount: 0,
        lastChecked: new Date(),
      });

      // Invalidate notifications query
      queryClient.invalidateQueries({
        queryKey: queryKeys.notifications.lists(),
      });
    } catch (_error) {
      console.error("Failed to mark all notifications as read:", _error);
    }
  }, [updateNotifications]);

  // Delete notification
  const deleteNotification = useCallback(
    async (id: string) => {
      try {
        await notificationsAPI.deleteNotification(id);

        // Optimistically update local state
        const deletedNotification = notifications.find((n) => n._id === id);
        setNotifications((prev) =>
          prev.filter((notification) => notification._id !== id)
        );

        // Update unread count if deleted notification was unread
        if (deletedNotification && !deletedNotification.is_read) {
          setUnreadCount((prev) => Math.max(0, prev - 1));
          updateNotifications({
            unreadCount: Math.max(0, unreadCount - 1),
            lastChecked: new Date(),
          });
        }

        // Invalidate notifications query
        queryClient.invalidateQueries({
          queryKey: queryKeys.notifications.lists(),
        });
      } catch (_error) {
        console.error("Failed to delete notification:", _error);
      }
    },
    [notifications, unreadCount, updateNotifications]
  );

  // Refresh notifications
  const refreshNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const response: NotificationsResponse =
        await notificationsAPI.getNotifications({
          page: 1,
          limit: 50,
        });

      // Safe destructuring with default values
      const {
        notifications: notificationsList,
        unread_count: unreadCountValue,
      } = response || { notifications: [], unread_count: 0 };

      setNotifications(notificationsList || []);
      setUnreadCount(unreadCountValue || 0);

      // Update app context
      updateNotifications({
        unreadCount: unreadCountValue || 0,
        lastChecked: new Date(),
      });
    } catch (_error) {
      console.error("Failed to fetch notifications:", _error);
      // Set default values on error
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [updateNotifications]);

  // Clear notifications from local state
  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Add new notification to local state
  const addNotification = useCallback(
    (notification: Notification) => {
      setNotifications((prev) => [notification, ...prev]);
      if (!notification.is_read) {
        setUnreadCount((prev) => prev + 1);
        updateNotifications({
          unreadCount: unreadCount + 1,
          lastChecked: new Date(),
        });
      }
    },
    [unreadCount, updateNotifications]
  );

  // Initialize notifications on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Set up real-time notifications (if WebSocket is available)
  useEffect(() => {
    // TODO: Implement WebSocket connection for real-time notifications
    // This would listen for new notifications and call addNotification

    return () => {
      // Cleanup WebSocket connection
    };
  }, [addNotification]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    isLoading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refreshNotifications,
    clearNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};

export default NotificationContext;
